import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { localSocket, onIdReady, pc_id, remoteSocket } from "../services/socket";
import { remote_url } from "../constants/api";
import { BrandLogo } from "../branding/BrandingContext";

const LauncherPage = () => {
    const navigate = useNavigate();

    const [isReady, setIsReady] = useState(
        localSocket.connected && remoteSocket.connected
    );

    const [selectedGame, setSelectedGame] = useState(() => localStorage.getItem('selected_game'));
    const bgImage = selectedGame === 'kanyonde' ? '/kanyonde_aviator_icon.jpg' : '/launcherBg.png';
    const [logo, setLogo] = useState(() => localStorage.getItem('logo') || "");

    const [countdown, setCountdown] = useState<number | null>(null);

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const launcherStartedAtRef = useRef(Date.now());
    const loginInFlightRef = useRef(false);
    const loggedInDeviceRef = useRef<string | null>(null);
    const launchScheduledRef = useRef(false);
    const storedDeviceCheckInFlightRef = useRef(false);
    const storedDeviceCheckedRef = useRef(false);
    const activationRedirectCheckedRef = useRef(false);

    const minimumLauncherMs = 4000;

    const resolveGameUrl = useCallback((game: string) => {
        switch (game) {
            case 'aviator':
                return 'https://aviator.pushvault.shop/game';
            case 'pilot':
                return 'https://game.pushvault.shop/game';
            case 'fury':
                return 'http://173.249.23.118:8089/';
            case 'nganya':
                return 'https://nganya.pushvault.shop/game';
            case 'kanyonde':
                return 'https://kanyonde.pushvault.shop';
            case 'chicken-road':
                return 'https://chickenroad.pushvault.shop';
            case 'sugar-daddy':
                return 'https://sugar.pushvault.shop/';
            default:
                return null;
        }
    }, []);

    const launchAfterRequiredInfo = useCallback(() => {
        if (launchScheduledRef.current || !loggedInDeviceRef.current) return;

        launchScheduledRef.current = true;
        setCountdown(Math.max(1, Math.ceil((minimumLauncherMs - (Date.now() - launcherStartedAtRef.current)) / 1000)));

        const elapsed = Date.now() - launcherStartedAtRef.current;
        const remainingLauncherMs = Math.max(0, minimumLauncherMs - elapsed);

        timerRef.current = setTimeout(() => {
            const selectedGame = localStorage.getItem('selected_game');
            const targetUrl = selectedGame ? resolveGameUrl(selectedGame) : null;

            if (selectedGame && selectedGame !== 'both' && targetUrl) {
                window.location.href = targetUrl;
            } else {
                navigate("/select-game");
            }
        }, remainingLauncherMs);
    }, [navigate, resolveGameUrl]);

    // =========================
    // LOGIN DEVICE
    // =========================
    const loginDevice = useCallback(async (deviceId: string | null) => {
        if (!deviceId) return false;

        if (loginInFlightRef.current || loggedInDeviceRef.current === deviceId) {
            return true;
        }

        loginInFlightRef.current = true;

        try {
            const request = await fetch(`${remote_url}/api/v1/device/login-device`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deviceId })
            });

            const data = await request.json();

            if (request.ok && data.success) {
                loggedInDeviceRef.current = deviceId;
                localStorage.setItem('token', data.token);

                if (data.data.stationId) {
                    localStorage.setItem('stationId', data.data.stationId);
                }

                launchAfterRequiredInfo();

                return true;
            }

            return false;
        } catch (err) {
            console.error("Login failed:", err);
            return false;
        } finally {
            loginInFlightRef.current = false;
        }
    }, [launchAfterRequiredInfo]);

    const fetchStoredDeviceId = useCallback(async () => {
        try {
            const response = await fetch('http://localhost:21578/getDeviceId');
            if (!response.ok) return null;

            const data = await response.json();

            return typeof data?.deviceId === 'string' && data.deviceId.trim()
                ? data.deviceId.trim()
                : null;
        } catch {
            return null;
        }
    }, []);

    // =========================
    // COUNTDOWN TICKER
    // =========================
    useEffect(() => {
        if (countdown === null || countdown <= 0) return;

        const interval = setInterval(() => {
            setCountdown(prev => (prev !== null ? prev - 1 : null));
        }, 1000);

        return () => clearInterval(interval);
    }, [countdown]);

    useEffect(() => {
        const bootstrapStoredDevice = async () => {
            if (
                !isReady ||
                loginInFlightRef.current ||
                loggedInDeviceRef.current ||
                storedDeviceCheckInFlightRef.current ||
                storedDeviceCheckedRef.current
            ) {
                return;
            }

            storedDeviceCheckInFlightRef.current = true;

            try {
                const storedDeviceId = await fetchStoredDeviceId();
                storedDeviceCheckedRef.current = true;

                if (storedDeviceId) {
                    await loginDevice(storedDeviceId);
                }
            } finally {
                storedDeviceCheckInFlightRef.current = false;
            }
        };

        void bootstrapStoredDevice();
    }, [fetchStoredDeviceId, isReady, loginDevice]);

    // =========================
    // INITIALIZATION
    // =========================
    useEffect(() => {
        const unsubscribeIdReady = onIdReady(async (readyPcId) => {
            console.log("System Initializing for PC_ID:", readyPcId);

            if (!isReady) return;

            const success = await loginDevice(readyPcId);

            if (!success) {
                const elapsed = Date.now() - launcherStartedAtRef.current;
                const remainingLauncherMs = Math.max(0, minimumLauncherMs - elapsed);

                setTimeout(() => {
                    if (!loggedInDeviceRef.current) {
                        navigate("/activation");
                    }
                }, remainingLauncherMs);
            }
        });

        return () => {
            unsubscribeIdReady();

            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = null;
        };
    }, [isReady, loginDevice, navigate]);

    useEffect(() => {
        if (
            !isReady ||
            pc_id ||
            loginInFlightRef.current ||
            storedDeviceCheckInFlightRef.current ||
            !storedDeviceCheckedRef.current ||
            activationRedirectCheckedRef.current
        ) {
            return;
        }

        const elapsed = Date.now() - launcherStartedAtRef.current;
        const remainingLauncherMs = Math.max(0, minimumLauncherMs - elapsed);

        const activationTimer = setTimeout(() => {
            activationRedirectCheckedRef.current = true;

            if (!pc_id && !loggedInDeviceRef.current && !loginInFlightRef.current) {
                navigate("/activation");
            }
        }, remainingLauncherMs);

        return () => clearTimeout(activationTimer);
    }, [isReady, navigate]);

    useEffect(() => {
        const handleSetGame = (data: any) => {
            const targetGame = data?.currentGame;
            if (!targetGame) return;

            if (targetGame === 'both') {
                localStorage.setItem('selected_game', 'both');
                setSelectedGame('both');

                if (loggedInDeviceRef.current) {
                    launchAfterRequiredInfo();
                }
                return;
            }

            localStorage.setItem('selected_game', targetGame);
            setSelectedGame(targetGame);

            const targetUrl = resolveGameUrl(targetGame);

            if (targetUrl && loggedInDeviceRef.current) {
                launchAfterRequiredInfo();
            }
        };

        remoteSocket.on('setGame', handleSetGame);

        return () => {
            remoteSocket.off('setGame', handleSetGame);
        };
    }, [launchAfterRequiredInfo, resolveGameUrl]);

    // =========================
    // LOGO HANDLING
    // =========================
    useEffect(() => {
        const handleActivation = async () => {
            await loginDevice(pc_id);
        };

        const setLogoImg = async (data: any) => {
            if (!data?.logoUrl) return;

            const freshUrl = `${data.logoUrl}${data.logoUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;

            try {
                const response = await fetch(freshUrl);

                if (!response.ok) {
                    throw new Error("Failed to fetch logo");
                }

                const blob = await response.blob();

                if (blob.size > 1_500_000) {
                    localStorage.setItem('logo', data.logoUrl);
                    setLogo(data.logoUrl);
                    return;
                }

                const reader = new FileReader();

                reader.onloadend = () => {
                    const base64 = reader.result as string;
                    const cachedLogo = localStorage.getItem('logo');

                    if (cachedLogo !== base64) {
                        localStorage.setItem('logo', base64);
                        setLogo(base64);
                        console.log("Logo updated successfully.");
                    }
                };

                reader.readAsDataURL(blob);
            } catch {
                setLogo(data.logoUrl);
                localStorage.setItem('logo', data.logoUrl);
            }
        };

        remoteSocket.on('deviceActivated', handleActivation);
        remoteSocket.on("deviceBranding", setLogoImg);

        return () => {
            remoteSocket.off('deviceActivated', handleActivation);
            remoteSocket.off("deviceBranding", setLogoImg);
        };
    }, [loginDevice]);

    // =========================
    // CONNECTION STATUS
    // =========================
    useEffect(() => {
        const checkStatus = () => {
            setIsReady(localSocket.connected && remoteSocket.connected);
        };

        localSocket.on('connect', checkStatus);
        localSocket.on('disconnect', checkStatus);
        remoteSocket.on('connect', checkStatus);
        remoteSocket.on('disconnect', checkStatus);

        checkStatus();

        return () => {
            localSocket.off('connect', checkStatus);
            localSocket.off('disconnect', checkStatus);
            remoteSocket.off('connect', checkStatus);
            remoteSocket.off('disconnect', checkStatus);
        };
    }, []);

    return (
        <div
            className="h-screen w-screen relative overflow-hidden bg-[#071626]"
            style={{ fontFamily: "'Born Strong', sans-serif" }}
        >
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat animate-slow-zoom"
                style={{ backgroundImage: `url('${bgImage}')` }}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-[#071626] via-[#071626]/20 to-black/60" />

            <div className="relative z-10 h-full flex flex-col justify-between p-12">
                <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-20 flex items-center justify-center bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                            {logo ? (
                                <img
                                    src={logo}
                                    alt="Logo"
                                    className="max-w-full max-h-full object-contain opacity-90 p-2"
                                    onError={() => setLogo("")}
                                />
                            ) : (
                                <BrandLogo className="max-w-full max-h-full text-3xl opacity-90 p-2" />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                        <div className="bg-black/40 border border-white/10 px-6 py-3 rounded-2xl flex items-center gap-4">
                            <div className="flex flex-col items-end">
                                <span className="text-[9px] text-slate-500 uppercase">
                                    System Status
                                </span>

                                <span
                                    className={`text-[11px] font-black uppercase ${
                                        isReady
                                            ? 'text-emerald-500'
                                            : 'text-red-600 animate-pulse'
                                    }`}
                                >
                                    {isReady ? 'All Systems Nominal' : 'Establishing Link...'}
                                </span>
                            </div>

                            <div
                                className={`w-3 h-3 rounded-full ${
                                    isReady
                                        ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]'
                                        : 'bg-red-600 animate-ping'
                                }`}
                            />
                        </div>

                        {countdown !== null && (
                            <div className="text-white/80 font-mono text-sm italic animate-pulse">
                                INITIALIZING GAME IN {countdown}S...
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-right">
                    <div className="text-[10px] font-mono text-white/20 mb-1">
                        DEVICE_ID
                    </div>

                    <div className="text-xs font-mono text-white/50 tracking-widest italic">
                        {pc_id}
                    </div>
                </div>
            </div>

            <style
                dangerouslySetInnerHTML={{
                    __html: `
                        @keyframes slow-zoom {
                            0% { transform: scale(1); }
                            100% { transform: scale(1.1); }
                        }

                        .animate-slow-zoom {
                            animation: slow-zoom 20s ease-in-out infinite alternate;
                        }
                    `
                }}
            />
        </div>
    );
};

export default LauncherPage;
